import {
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * TypingIndicatorService - Manages real-time typing indicators
 *
 * Features:
 * - Start/stop typing indicators
 * - Real-time subscription to typing status
 * - Auto-expiry after 10 seconds
 * - Filters out expired indicators
 *
 * Usage:
 * - Call startTyping() when user begins typing
 * - Call stopTyping() when user stops or sends message
 * - Subscribe with subscribeToTyping() for real-time updates
 * - Indicators auto-expire after 10 seconds if not renewed
 *
 * Implementation:
 * - Uses Firestore onSnapshot() for real-time updates
 * - Stores typing state per conversation
 * - Client-side filtering removes expired indicators
 * - Minimal Firestore writes for cost efficiency
 */
class TypingIndicatorServiceImpl {
  private readonly COLLECTION_NAME = 'typingIndicators';
  private readonly EXPIRY_SECONDS = 10;

  /**
   * Start typing indicator for a user in a conversation
   *
   * @param conversationId Conversation ID
   * @param userId User ID
   */
  async startTyping(conversationId: string, userId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, conversationId);
      const expiresAt = new Date(Date.now() + this.EXPIRY_SECONDS * 1000);

      await setDoc(docRef, {
        [`typing.${userId}`]: {
          startedAt: Timestamp.now(),
          expiresAt: Timestamp.fromDate(expiresAt),
        },
      }, { merge: true });
    } catch (error: any) {
      console.error('Error starting typing indicator:', error);
      // Don't throw - typing indicators are non-critical
    }
  }

  /**
   * Stop typing indicator for a user in a conversation
   *
   * @param conversationId Conversation ID
   * @param userId User ID
   */
  async stopTyping(conversationId: string, userId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, conversationId);

      // Delete the user's typing indicator by setting to null
      await updateDoc(docRef, {
        [`typing.${userId}`]: null,
      });
    } catch (error: any) {
      console.error('Error stopping typing indicator:', error);
      // Don't throw - typing indicators are non-critical
    }
  }

  /**
   * Subscribe to typing indicators for a conversation
   *
   * @param conversationId Conversation ID
   * @param callback Callback with array of user IDs currently typing
   * @returns Unsubscribe function
   */
  subscribeToTyping(
    conversationId: string,
    callback: (userIds: string[]) => void
  ): Unsubscribe {
    const docRef = doc(db, this.COLLECTION_NAME, conversationId);

    return onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          callback([]);
          return;
        }

        const data = snapshot.data();
        const typing = data?.typing || {};
        const now = Date.now();

        // Filter out expired typing indicators
        const activeTypers = Object.entries(typing)
          .filter(([userId, data]: [string, any]) => {
            if (!data || !data.expiresAt) return false;

            const expiresAt = data.expiresAt?.toDate().getTime();
            return expiresAt && expiresAt > now;
          })
          .map(([userId]) => userId);

        callback(activeTypers);
      },
      (error) => {
        console.error('Error subscribing to typing indicators:', error);
        callback([]);
      }
    );
  }

  /**
   * Clear all typing indicators for a conversation
   * (Useful for cleanup or when conversation is closed)
   *
   * @param conversationId Conversation ID
   */
  async clearAllTyping(conversationId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, conversationId);

      await setDoc(docRef, {
        typing: {},
      });
    } catch (error: any) {
      console.error('Error clearing typing indicators:', error);
      // Don't throw - typing indicators are non-critical
    }
  }

  /**
   * Clear expired typing indicators manually
   * (Optional - normally handled client-side in subscriptions)
   *
   * @param conversationId Conversation ID
   * @param currentTypers Array of user IDs to keep (remove others)
   */
  async clearExpiredTyping(conversationId: string, currentTypers: string[]): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, conversationId);

      // Build update object to remove typing indicators not in currentTypers
      const updates: any = { typing: {} };
      currentTypers.forEach(userId => {
        updates.typing[userId] = {
          startedAt: Timestamp.now(),
          expiresAt: Timestamp.fromDate(new Date(Date.now() + this.EXPIRY_SECONDS * 1000)),
        };
      });

      await setDoc(docRef, updates);
    } catch (error: any) {
      console.error('Error clearing expired typing indicators:', error);
      // Don't throw - typing indicators are non-critical
    }
  }
}

export const TypingIndicatorService = new TypingIndicatorServiceImpl();
